import { FC } from 'react';
import { RecommendGrid } from 'components/RecommendGrid';
import { memberInfoSelector } from '@doda/common/src/redux/features/memberInfo';
import { useAppSelector } from 'redux/hooks';
import { formatString } from '@doda/common/src/utils/stringUtil';
import { KeywordsResultResponse } from '@doda/api-client/aspida/@types';
import { VisibleAnalytics } from '@doda/common/src/components/Analytics/VisibleAnalytics';
import { measureVisibleRecommend } from '@doda/common/src/logics/analytics';
import { RecommendSlider } from 'components/RecommendSlider';
import styles from './index.module.scss';
import { ADDITIONAL_TITLE_QUERY, BOOK_MARK_URL } from './constants';
import { useRecommend } from './hooks/useRecommend';
import { useRecommendListAnalytics } from './hooks/useRecommendListAnalytics';
import { useB4rTsuukaRecommendMeasure } from './hooks/useB4rTsuukaRecommendMeasure';
import { B4rTsuukaAbTestPattern } from '../../types/analytics';

/**
 * RecommendListのProps
 * @param similarKeywordList 類似キーワードリスト
 * @param b4rTsuukaABTestPattern AB test type for B4R recommend
 */
interface Props {
  similarKeywordList: KeywordsResultResponse[];
  b4rTsuukaABTestPattern: B4rTsuukaAbTestPattern;
}

export const RecommendList: FC<Props> = ({
  similarKeywordList,
  b4rTsuukaABTestPattern,
}) => {
  const loginStatus = useAppSelector(memberInfoSelector)?.loginStatus ?? false;
  const { recommendForList, recommendForSlider } = useRecommend(
    b4rTsuukaABTestPattern
  );
  const {
    onClickBookmark,
    onClickRecommendCardText,
    onClickRecommendCardThumbnail,
  } = useRecommendListAnalytics({
    recommendForList,
    recommendForSlider,
    similarKeywordList,
  });

  // DODASITE-17171 求人一覧_応募B4R VS B4R通過モデル
  const type =
    recommendForList?.recommendType || recommendForSlider?.recommendType;
  useB4rTsuukaRecommendMeasure(type, b4rTsuukaABTestPattern);

  if (!recommendForList && !recommendForSlider) return null;

  // 通常のおすすめ求人
  if (recommendForList) {
    const { title, results, recommendType, effectName } = recommendForList;

    return (
      <div className={styles.jobSearchList__recommend__wrapper}>
        <VisibleAnalytics
          onVisible={() => measureVisibleRecommend(recommendType, results)}
        >
          <RecommendGrid
            title={title}
            recommendList={results}
            recommendType={recommendType}
            saveBookmarkRequestBody={{ device: 'PC' }}
            onClickSaved={() => null}
            onClickCardDetail={(_, index, jid) =>
              onClickRecommendCardText(index, jid, recommendType, effectName)
            }
            onClickThumbnail={(_, index, jid) =>
              onClickRecommendCardThumbnail(
                index,
                jid,
                recommendType,
                effectName
              )
            }
            onClickFavorite={onClickBookmark}
            bookmarkListUrl={formatString(BOOK_MARK_URL, [
              loginStatus ? 'login' : 'logout',
            ])}
            additionalQueryString={formatString(ADDITIONAL_TITLE_QUERY, [
              loginStatus ? 'login' : 'logout',
            ])}
          />
        </VisibleAnalytics>
      </div>
    );
  }
  // デフォルトのおすすめ求人
  if (recommendForSlider) {
    const { title, results, recommendType, effectName } = recommendForSlider;

    return (
      <VisibleAnalytics
        onVisible={() => measureVisibleRecommend(recommendType, results)}
      >
        <div className={styles.jobSearchList__recommend__wrapper}>
          <RecommendSlider
            recommendList={results}
            title={title}
            recommendType={recommendType}
            onClickSaved={undefined}
            bookmarkListUrl={formatString(BOOK_MARK_URL, [
              loginStatus ? 'login' : 'logout',
            ])}
            saveBookmarkRequestBody={{ device: 'PC' }}
            isSummaryBottom={false}
            onClickCardDetail={(_, index, jid) =>
              onClickRecommendCardText(index, jid, recommendType, effectName)
            }
            onClickThumbnail={(_, index, jid) =>
              onClickRecommendCardThumbnail(
                index,
                jid,
                recommendType,
                effectName
              )
            }
            onClickFavorite={onClickBookmark}
            additionalQueryString={formatString(ADDITIONAL_TITLE_QUERY, [
              loginStatus ? 'login' : 'logout',
            ])}
          />
        </div>
      </VisibleAnalytics>
    );
  }

  return null;
};
